import { useEffect, useState, useRef } from "react";
import { X } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

export default function GuidedTour({ steps, onAction, onClose }) {
  const { t } = useI18n();
  const [i, setI] = useState(0);
  const [pos, setPos] = useState(null);
  const elRef = useRef(null);
  const step = steps[i];

  useEffect(() => {
    let cancelled = false;
    let tries = 0;
    if (elRef.current) elRef.current.classList.remove("fp-highlight");
    if (onAction) onAction(step);

    // Poll for the target element (panels/modules may mount with a delay/animation).
    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(`[data-testid="${step.testid}"]`);
      if (el) {
        elRef.current = el;
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("fp-highlight");
        const r = el.getBoundingClientRect();
        const top = Math.min(Math.max(r.bottom + 10, 16), window.innerHeight - 200);
        const left = Math.min(Math.max(r.left, 16), window.innerWidth - 344);
        setPos({ top, left });
      } else if (tries < 24) {
        tries += 1;
        setTimeout(tick, 120);
      } else {
        setPos(null); // fallback: centered tooltip
      }
    };
    const t0 = setTimeout(tick, 260);
    return () => { cancelled = true; clearTimeout(t0); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i]);

  const finish = () => {
    if (elRef.current) elRef.current.classList.remove("fp-highlight");
    onClose();
  };

  const tip = pos || { top: window.innerHeight / 2 - 90, left: window.innerWidth / 2 - 160 };

  return (
    <div className="fixed inset-0 z-[950]" style={{ pointerEvents: "none" }} data-testid="guided-tour">
      <div className="absolute w-[324px] bg-slate-800 border border-cyan-600 rounded-lg shadow-2xl p-4"
        style={{ top: tip.top, left: tip.left, pointerEvents: "auto" }}>
        <button onClick={finish} data-testid="tour-x" className="absolute top-2 right-2 text-slate-500 hover:text-white"><X size={16} /></button>
        <div className="text-[11px] text-cyan-400 mb-1">{t("Krok")} {i + 1} / {steps.length}</div>
        <div className="font-heading font-bold text-sm mb-1 pr-4">{t(step.title)}</div>
        <div className="text-sm text-slate-300 leading-relaxed mb-3">{t(step.text)}</div>
        <div className="flex items-center justify-between gap-2">
          <button onClick={finish} data-testid="tour-close" className="text-xs text-slate-400 hover:text-white">{t("Zakończ")}</button>
          <div className="flex gap-2">
            <button disabled={i === 0} onClick={() => setI(i - 1)} data-testid="tour-prev"
              className="text-xs px-2.5 py-1 rounded border border-slate-600 text-slate-200 disabled:opacity-40">{t("Wstecz")}</button>
            {i < steps.length - 1
              ? <button onClick={() => setI(i + 1)} data-testid="tour-next" className="text-xs px-2.5 py-1 rounded bg-cyan-600 text-white hover:bg-cyan-500">{t("Dalej")}</button>
              : <button onClick={finish} data-testid="tour-finish" className="text-xs px-2.5 py-1 rounded bg-green-600 text-white hover:bg-green-500">{t("Gotowe")}</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
